// @ts-strict-ignore
import { Features } from '@backend/lib-feature-mgr';
import { useRouter } from 'next/router';
import { styled } from 'styled-components';

import { AddressSelect } from '~/components/AddressSelect';
import { HeaderFormField } from '~/components/Header/HeaderFormField';
import { JobSelect } from '~/components/JobSelect';
import { SearchButton } from '~/components/SearchButton';
import { useSearchFormContext } from '~/contexts/SearchFormContext/SearchFormContext';
import { useCheckFeatureEnabled, useEffectIfMounted, useIsMobileOrTablet } from '~/hooks';
import { tabletAndMobileOnlyMixin } from '~/utils/mixins';
import { getMediaQuery } from '~/utils/responsive/media';

export const StyledHeaderForm = styled.div<{ isSearchApplied: boolean | undefined }>`
  display: flex;
  gap: 32px;
  align-items: end;

  ${getMediaQuery('mobile')} {
    display: ${props => (props.isSearchApplied ? 'none' : 'flex')};
  }

  ${getMediaQuery('mobile')} {
    align-items: stretch;
    flex-direction: column;
  }
`;
export const StyledHeaderFormColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledSearchButton = styled(SearchButton)`
  ${tabletAndMobileOnlyMixin};

  ${getMediaQuery('mobile')} {
    flex: 1;
  }
`;

type HeaderFormProps = {
  isSearchApplied?: boolean;
};

export const HeaderForm = ({ isSearchApplied }: HeaderFormProps) => {
  const { asPath } = useRouter();
  const isMobileOrTablet = useIsMobileOrTablet();
  const locationMandatoryFeature = useCheckFeatureEnabled(Features.JobBoardLocationMandatory);

  const {
    form: {
      job: { value: job, error: jobError },
      address: { value: address, error: addressError },
    },
    submit,
    resetForm,
  } = useSearchFormContext();

  useEffectIfMounted(() => {
    if (asPath === '/') {
      resetForm();
    }
  }, [asPath]);

  useEffectIfMounted(async () => {
    if (!isMobileOrTablet) {
      const hasAddress = address?.name && !addressError;
      const hasJob = job && !jobError;
      if (hasAddress || (!locationMandatoryFeature && hasJob)) {
        await submit();
      }
    }
  }, [isMobileOrTablet, address?.name, addressError, job, jobError]);

  return (
    <StyledHeaderForm isSearchApplied={isSearchApplied}>
      <StyledHeaderFormColumn>
        <HeaderFormField type="address">
          <AddressSelect fontSize="l" />
        </HeaderFormField>
      </StyledHeaderFormColumn>
      <StyledHeaderFormColumn>
        <HeaderFormField type="job">
          <JobSelect fontSize="l" />
        </HeaderFormField>
      </StyledHeaderFormColumn>
      <StyledSearchButton />
    </StyledHeaderForm>
  );
};
